import { IDashboardConfig } from '@/store/models/dashboard-config.model';

export const initializeDashboardConfig = (config: IDashboardConfig): IDashboardConfig => ({
  ...config,
  site: {
    ...config.site,
    keywords: config.site.keywords || 'Treezor, Fintech, Dashboard, Connect',
    description:
      config.site.description ||
      'Treezor, avec sa plateforme technique et ses agréments, permet d’offrir un service en one-stop shop à ses clients.',
    logo: {
      ...(config.site.logo || {}),
      full: config.site.logo?.full || 'https://www.treezor.com/app/uploads/2021/02/logo-blanc-svg-NEW.svg',
    },
  },
  support: {
    ...(config.support || {}),
    zendesk: {
      ...(config.support?.zendesk || {}),
      key: config.support?.zendesk?.key || '898b126d-86b4-44a3-8ced-23572b1bf135',
      forms: config.support?.zendesk?.forms || [
        360000892280, 360000902940, 360001095340, 360001095360, 360001642839, 360001642859, 360001694660,
      ],
    },
  },
  settings: {
    ...config.settings,
    payouts: {
      ...config.settings.payouts,
      controls: config.settings.payouts?.controls || {
        amountPhysicalUser: 10000,
        amountBusinessUser: 50000,
      },
    },
    users: {
      ...(config?.settings.users || {}),
      kyc: {
        ...config?.settings?.users?.kyc,
        useAdditionalVigilanceMeasure: true,
      },
      tabs: config?.settings?.users?.tabs || [
        'overview',
        'kyckyb',
        'children',
        'companyMembers',
        'wallets',
        'cards',
        'beneficiaries',
      ],
      actions: config?.settings?.users?.actions || [
        'deleteUser',
        'sendForgotPassword',
        'addKycDocument',
        'reviewKyc',
        'downloadOperations',
        'transfer',
        'payout',
        'addWallet',
        'addBeneficiary',
        'addChild',
      ],
    },
    cards: {
      ...config.settings.cards,
      defaultDeliveryAddress: {
        ...(config.settings.cards.defaultDeliveryAddress || {
          address1: '33 avenue Wagram',
          postCode: '75017',
          city: 'Paris',
          country: 'FR',
        }),
      },
      designs: config.settings.cards.designs,
      restrictions: config.settings.cards.restrictions || ['mid', 'mcc', 'country'],
    },
    wallets: {
      ...config?.settings?.wallets,
      types: config.settings.wallets.types || [
        { label: 'e-Money', value: 9 },
        { label: 'Payment', value: 10 },
        { label: 'Technical', value: 15 },
        { label: 'Mirror', value: 13 },
        { label: 'Electronic Money Card', value: 14 },
      ],
      options: config.settings.wallets.options || [
        'details',
        'accountDetails',
        'edit',
        'statement',
        'deactivate',
        'simulateSCTR',
        'simulateOperation',
      ],
    },
    screens: config.settings.screens || [
      'search',
      'profiles',
      'allUsers',
      'createUser',
      'allCardTransactions',
      'allCards',
      'kycNeedReview',
      'bulkCards',
      'onboarding',
      'supportUsers',
      'restrictions',
      'webhooks',
      'templates',
      'configurations',
      'allBeneficiaries',
    ],
    searchBar: config.settings.searchBar || [
      'userId',
      'userTag',
      'email',
      'userName',
      'walletId',
      'walletIban',
      'cardToken',
      'cardEmbossedName',
      'beneficiaryId',
      'beneficiaryIban',
      'beneficiaryName',
    ],
    beneficiaries: config.settings.beneficiaries || ['scte', 'sddr'],
    configuration: {
      ...config.settings.configuration,
      tabs: config.settings.configuration?.tabs || ['settings', 'security', 'integration'],
    },
    bulkCards: {
      ...(config.settings?.bulkCards || {}),
      permsGroup: config.settings?.bulkCards?.permsGroup || ['nfc', 'atm', 'online', 'foreign'],
    },
  },
});

export const validateDashboardConfig = (config: IDashboardConfig): boolean | string[] => {
  const missingConfigs: string[] = [];

  // Config Site
  !config?.site && missingConfigs.push('site');

  !config?.site?.name && missingConfigs.push('site.name');
  !config?.site?.environment && missingConfigs.push('site.environment');
  // Config API
  !config?.api && missingConfigs.push('api');
  !config?.api?.baseUrl && missingConfigs.push('api.baseUrl');

  !config?.api?.oauth && missingConfigs.push('api.oauth');
  !config?.api?.oauth?.scopes && missingConfigs.push('api.oauth.scopes');
  !config?.api?.oauth?.availableScopes && missingConfigs.push('api.oauth.availableScopes');
  !config?.api?.oauth?.clientId && missingConfigs.push('api.oauth.clientId');
  !config?.api?.oauth?.redirectUri && missingConfigs.push('api.oauth.redirectUri');
  !config?.api?.oauth?.path && missingConfigs.push('api.oauth.path');

  !config?.api?.sso && missingConfigs.push('api.oauth');
  !config?.api?.sso?.scopes && missingConfigs.push('api.oauth.scopes');
  !config?.api?.sso?.clientId && missingConfigs.push('api.oauth.clientId');
  !config?.api?.sso?.redirectUri && missingConfigs.push('api.oauth.redirectUri');
  !config?.api?.sso?.path && missingConfigs.push('api.oauth.path');
  // Config Settings
  !config?.settings && missingConfigs.push('settings');

  !config?.settings?.cards && missingConfigs.push('settings.cards');
  !config?.settings?.cards?.designs && missingConfigs.push('settings.cards.designs');

  !config?.settings?.wallets && missingConfigs.push('settings.wallets');
  !config?.settings?.wallets?.types && missingConfigs.push('settings.wallets.types');

  if (missingConfigs.length === 0) return true;

  console.error({ missingConfigs });
  return missingConfigs;
};
